<template>
	<Flyout
		:title="'Edit 3P Vendor Details'"
		:parentTag="'form'"
		:display="display"
		:novalidate="'novalidate'"
		@show="cancelAndSaveAction"
	>
		<template #flyout-content>
			<Spinner :processor="{ loading: loading }" />
			<div class="edit-vendor-details">
				<div class="edit-vendor-details__fields">
					<div class="edit-vendor-details__fields-group">
						<Input
							className="field__input"
							type="text"
							label="Vendor Name"
							name="Name"
							inputType="text"
							:maxLength="200"
							:required="true"
							v-model="vendorData.integratorName"
							@hasError="handleError"
						/>
						<Input
							className="field__input"
							type="text"
							label="Alias Name"
							name="Alias"
							inputType="text"
							:maxLength="100"
							v-model="vendorData.alias"
						/>
						<Datepicker
							labelText="Partner Since"
							datePickerType="singleDate"
							v-model="vendorData.dateActive"
						/>
						<SegmentedControl
							labelText="Is this 3P Vendor an Integration Solution?"
							:options="integrationSolutionOptions"
							:required="true"
							v-model="vendorData.dmsis"
						/>
					</div>
					<div class="edit-vendor-details__fields-group">
						<Input
							className="field__input"
							label="Website"
							name="Website"
							inputType="url"
							:maxLength="200"
							v-model="vendorData.website"
							@hasError="handleError"
						/>
						<Input
							className="field__input"
							label="Confluence"
							name="Confluence"
							inputType="url"
							:maxLength="250"
							v-model="vendorData.confluenceLink"
							@hasError="handleError"
						/>
					</div>
					<div class="edit-vendor-details__fields-group">
						<Input
							className="field__input"
							label="Address 1"
							name="Address 1"
							:maxLength="100"
							:required="isAddressRequired"
							v-model="vendorData.address1"
							@hasError="handleError"
						/>
						<Input
							className="field__input"
							label="Address 2"
							name="Address 2"
							:maxLength="100"
							v-model="vendorData.address2"
						/>
						<div class="edit-vendor-details__fields-grid">
							<Input
								className="field__input"
								label="City"
								name="City"
								:maxLength="100"
								:required="isAddressRequired"
								v-model="vendorData.city"
								@hasError="handleError"
							/>
							<Dropdown
								label="State"
								name="ddState"
								:autosuggestion="true"
								:defaultValue="'Select One'"
								:options="stateOptions"
								:required="isAddressRequired"
								v-model="vendorData.state"
								@hasError="handleError"
							/>
							<Input
								className="field__input"
								label="Zip"
								name="Zip"
								:maxLength="6"
								inputType="numeric"
								:required="isAddressRequired"
								v-model="vendorData.zip"
								@hasError="handleError"
							/>
						</div>
					</div>
					<Textarea
						label="3P Vendor Description"
						:cols="50"
						:rows="5"
						:maxLength="200"
						v-model="vendorData.description"
					/>
					<Textarea
						label="Internal Notes"
						:cols="50"
						:rows="8"
						:maxLength="500"
						v-model="vendorData.internalNotes"
					/>
				</div>
				<Modals
					v-model="showConfirmationSaveModal"
					:type="'confirmation'"
					:title="'Would you like to save changes before exiting?'"
					:confirmationContent="'Unsaved changes will be lost.'"
					:labelConfirmButton="'Yes, save changes'"
					:labelCancelButton="'Cancel'"
					@confirm="saveAction"
					@cancel="cancelAndClose"
				>
				</Modals>
			</div>
		</template>

		<template #flyout-footer>
			<ButtonGroup
				:items="[
					{
						btnClick: saveAction,
						className: 'btn--primary',
						labelText: 'Save Changes',
						disabled: disabledSaveAction
					},
					{
						btnClick: cancelAndSaveAction,
						className: 'btn btn--link',
						labelText: 'Cancel'
					}
				]"
			/>
		</template>
	</Flyout>
</template>

<script>
import Flyout from '@/components/shells/flyout/flyout';
import Spinner from '@/components/visual/spinner/spinner';
import ButtonGroup from '@/components/forms/button/button-group';
import Input from '@/components/forms/input/input';
import Textarea from '@/components/forms/textarea/textarea.vue';
import Datepicker from '@/components/forms/date-picker/date-picker';
import Dropdown from '@/components/forms/dropdown/dropdown.vue';
import Modals from '@/components/shells/modal/modals';
import SegmentedControl from '@/components/forms/segmented-control/segmented-control.vue';
import { MODULE_NAME, VENDOR, SAVE_VENDOR } from '../vendors-mutation-types';
import {
	MODULE_NAME as OPTIONS_MODULE_NAME,
	STATES,
	SET_STATES
} from '@/sections/options/options-mutation-type';
import { mapGetters, mapActions } from 'vuex';
import { processCreatePatch } from '@/utils/function';
import _ from 'lodash';

export default {
	name: 'VendorDetailsFlyout',
	components: {
		Flyout,
		Spinner,
		ButtonGroup,
		Input,
		Textarea,
		Datepicker,
		Dropdown,
		Modals,
		SegmentedControl
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		display: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			originalVendor: {},
			vendorData: {},
			loading: false,
			hasError: false,
			hasVendorChanges: false,
			showConfirmationSaveModal: false,
			integrationSolutionOptions: [
				{
					text: 'Yes',
					value: true
				},
				{
					text: 'No',
					value: false
				}
			]
		};
	},
	computed: {
		...mapGetters(MODULE_NAME, [VENDOR]),
		...mapGetters(OPTIONS_MODULE_NAME, [STATES]),
		disabledSaveAction() {
			const hasEmptyValue = objeto => {
				return Object.values(objeto).some(
					val => val === '' || val === null || val === undefined
				);
			};

			if (Object.keys(this.vendorData).length === 0) return true;

			if (!this.hasVendorChanges || this.hasError) {
				return true;
			}

			const addressObj = {
				address1: this.vendorData.address1,
				city: this.vendorData.city,
				state: this.vendorData.state,
				zip: this.vendorData.state
			};

			if (this.isAddressRequired && hasEmptyValue(addressObj)) {
				return true;
			}

			if (this.vendorData.address2 && hasEmptyValue(addressObj)) {
				return true;
			}

			return false;
		},
		stateOptions() {
			if (this[STATES].length > 0) {
				return this[STATES].map(x => {
					return {
						text: x.description,
						value: x.value
					};
				});
			}
			return [];
		},
		isAddressRequired() {
			if (
				this.vendorData.address1 ||
				this.vendorData.city ||
				this.vendorData.state ||
				this.vendorData.zip
			) {
				return true;
			}
			return false;
		}
	},
	methods: {
		...mapActions(MODULE_NAME, {
			saveVendorAction: SAVE_VENDOR
		}),
		...mapActions(OPTIONS_MODULE_NAME, [SET_STATES]),
		async saveAction() {
			const self = this;
			self.loading = true;
			self.showConfirmationSaveModal = false;

			const parseVendor = processCreatePatch(
				this.originalVendor,
				this.vendorData
			);
			await this.saveVendorAction({
				id: this.vendorData.id,
				data: parseVendor
			})
				.then(() => {
					self.cancelAndClose();
				})
				.catch()
				.finally(() => {
					self.loading = false;
				});
		},
		cancelAndSaveAction(e) {
			if (e) e.preventDefault();
			if (this.hasVendorChanges) {
				this.showConfirmationSaveModal = true;
				return;
			}
			this.cancelAndClose();
		},
		cancelAndClose() {
			this.$emit('show', false);
		},
		handleError(obj) {
			this.hasError =
				obj['Website'] ||
				obj['Confluence'] ||
				obj['Name'] ||
				obj['Address 1'] ||
				obj['City'] ||
				obj['State'] ||
				obj['Zip'];
		}
	},
	watch: {
		display: {
			immediate: true,
			deep: true,
			async handler(val) {
				if (val) {
					if (_.isEmpty(this[VENDOR])) {
						this.$store.dispatch('toastMessages/show', {
							toastId: 'vendor.details',
							statusCode: 400,
							message: 'Vendor data is empty',
							slot: 'default',
							useIcon: true,
							userDismissable: true
						});

						this.cancelAndClose();
						return;
					}
					if (this[STATES].length == 0) {
						const self = this;
						self.loading = true;
						await this[SET_STATES]().finally(() => {
							self.loading = false;
						});
					}
					this.originalVendor = _.cloneDeep(this[VENDOR]);

					if (this.originalVendor.dateActive) {
						this.originalVendor.dateActive = new Date(
							this.originalVendor.dateActive
						);
					}
					this.vendorData = _.cloneDeep(this.originalVendor);
				}
			}
		},
		vendorData: {
			deep: true,
			handler(obj) {
				const isequal = _.isEqual(this.originalVendor, obj);
				this.hasVendorChanges = !isequal;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * [Description]
    *
    * Edit 3P Vendor Details Flyout
    * Can use SCSS variables = TRUE
    *
 **/

.edit-vendor-details {
	&__fields {
		display: flex;
		flex-direction: column;
		gap: $gutter-xxl;
	}
	&__fields-group {
		display: grid;
		grid-template-columns: 1fr;
		gap: $gutter-lg;
	}
	&__fields-grid {
		display: grid;
		grid-template-columns: 40% 35% auto;
		column-gap: $gutter-lg;
	}
}

::v-deep .flyout__body {
	@include scrollbar;
}
</style>
